import Joi from 'joi';
import moment from 'moment';
import React, { ChangeEvent, Component, KeyboardEvent, } from 'react';
import { RouteComponentProps } from 'react-router';
import ProviderDTO from '../../../../common/api/dtos/Provider';
import { addProvider, listProviderDetails, updateProvider } from '../../../../common/api/endpoints/providers';
import AppContext from '../../../../common/context/AppContext';
import { FormErrors } from '../../../../common/data/FormErrors';
import { processJoiError } from '../../../../common/helpers/processJoiError';
import { TRequestStatus } from '../../../../common/types/RequestStatus';
import ButtonControl from '../../../controls/ButtonControl/ButtonControl';
import ToggleControl from '../../../controls/ToggleControl/ToggleControl';
import BreadcrumbControls from '../../../generics/Header/BreadcrumbControls';
import ProgressBar, { IProgress, incrementProgress } from '../../../utils/ProgressBar/ProgressBar';
import RequestStatus from '../../../utils/RequestStatus/RequestStatus';
import { isEditPage } from '../../../../common/helpers/isEditPage';
import TextAreaControl from '../../../controls/TextAreaControl/TextAreaControl';
import { loadComplete } from '../../../../common/helpers/LoadComplete';
import TextControl from '../../../controls/TextControl/TextControl';
import SelectControl from '../../../controls/SelectControl/SelectControl';
import { listTenants } from '../../../../common/api/endpoints/tenants';
import TenantDTO from '../../../../common/api/dtos/Tenant';
import { withTransitionEvent } from '../../../TransitionEvent';

export interface Props {
  id: string,
}

interface FormData {
  name: string,
  iconUrl: string,
  description: string,
  comments: string,
  tileColor: string,
  lastUpdate: string,
  tenantId?: number | null,
  deleted: boolean,
}

interface State {
  providerId: string,
  progress: IProgress,
  pageStatus: TRequestStatus,
  formStatus: TRequestStatus,
  formData: FormData,
  providers?: ProviderDTO[],
  tenants?: TenantDTO[],
  formErrors: StateFormErrors,
}

type StateFormErrors = {
  [key in keyof FormData]?: string;
}

class ProviderDetail extends Component<RouteComponentProps<Props>, State> {
  formSchema = Joi.object({
    name: Joi.string().trim(true).required().messages(FormErrors.string),
    iconUrl: Joi.string().trim(true).allow(null, ''),
    description: Joi.string().trim(true).allow(null, ''),
    comments: Joi.string().trim(true).allow(null, ''),
    tileColor: Joi.string().trim(true).allow(null, ''),
    lastUpdate: Joi.string().trim(true).allow(null, ''),
    tenantId: Joi.number().allow(null, 0),
    deleted: Joi.boolean(),
  });

  constructor(props: RouteComponentProps<Props>) {
    super(props);
    this.state = {
      providerId: this.props.match.params.id,
      formData: {
        name: '',
        iconUrl: 'inventory.png',
        description: '',
        comments: '',
        tileColor: '',
        lastUpdate: moment(new Date()).format('Y-MM-DD'),
        deleted: false,
      },
      progress: {
        currentStep: 0,
        totalSteps: 2
      },
      pageStatus: 'loading',
      formStatus: 'idle',
      formErrors: {},
    }
  }

  fetchProvidersDetails = async () => {
    this.setState({
      pageStatus: 'loading'
    });

    const provider = await listProviderDetails(this.state.providerId);

    this.setState(prevState => {
      return {
        pageStatus: 'success',
        formData: {
          name: provider.name,
          iconUrl: provider.iconUrl,
          description: provider.description,
          comments: provider.comments,
          tileColor: provider.tileColor,
          lastUpdate: moment(new Date(provider.lastUpdate)).format('Y-MM-DD'),
          tenantId: provider.tenantId ?? null,
          deleted: provider.deleted,
        },
        progress: incrementProgress(prevState.progress),
      }
    });
  }

  fetchTenants = async () => {
    this.setState({
      pageStatus: 'loading'
    });

    const tenants = await listTenants();

    this.setState(prevState => {
      return {
        pageStatus: 'success',
        tenants,
        progress: incrementProgress(prevState.progress),
      }
    });
  }

  addEntity = async () => {
    this.setState({
      formStatus: 'loading'
    });

    let result = await addProvider(this.state.formData);

    if ('error' in result) {
      if (result.error.code === 'unique') {
        this.updateFormError(result.error.field, 'InternalId must be unique');
      }
      this.setState({ formStatus: 'error' });
    } else {

      this.setState({
        formStatus: 'success',
      }, () => {
        this.props.history.push({
          pathname: '/provider'
        })
      });
    }
  }

  updateEntity = async () => {
    this.setState({
      formStatus: 'loading'
    });

    let result = await updateProvider(this.state.formData, this.state.providerId);
    if ('error' in result) {
      if (result.error.code === 'unique') {
        this.updateFormError(result.error.field, 'InternalId must be unique');
      }
      this.setState({ formStatus: 'error' });
    } else {
      this.setState({
        formStatus: 'success',
        formData: {
          ...this.state.formData,
          lastUpdate: moment(new Date()).format('Y-MM-DD'),
        }
      });
    }
  }

  handleSubmit = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    ev.preventDefault();
    const isValid = this.validateForm();

    if (isValid) {
      this.setState({
        formStatus: 'loading',
      });

      if (!isEditPage(Number(this.state.providerId))) {
        this.addEntity();
      } else {
        this.updateEntity();
      }
    }
  }

  validateFormField = <K extends keyof FormData>(field: K) => {
    const subSchema = this.formSchema.extract(field);
    const result = subSchema.validate(this.state.formData[field], { abortEarly: false });

    if (result.error) {
      this.updateFormError(field, result.error.message);
    } else {
      this.updateFormError(field, "");
    }
  }

  validateForm = () => {
    // reset form errors
    this.setState({
      formErrors: {}
    });

    const result = this.formSchema.validate(this.state.formData, { abortEarly: false });
    if (result.error) {
      const formErrors = processJoiError(result.error);
      this.setState({
        // Assume type based on formSchema and Joi's error
        formErrors: formErrors as StateFormErrors,
      });

      return false;
    }

    return true;
  }

  componentDidMount() {
    this.fetchTenants();

    if (isEditPage(Number(this.state.providerId))) {
      this.setState((prevState: State) => {
        return {
          progress: {
            currentStep: prevState.progress.currentStep,
            totalSteps: 2
          }
        }
      });
      this.fetchProvidersDetails()
    } else {
      this.setState((prevState: State) => {
        return {
          progress: {
            currentStep: prevState.progress.currentStep,
            totalSteps: 1
          }
        }
      });
    }
  }

  updateFormError<K extends keyof StateFormErrors>(field: K, value: StateFormErrors[K]) {
    this.setState(prevState => {
      return {
        formErrors: {
          ...prevState.formErrors,
          [field]: value,
        }
      }
    })
  }

  updateFormData<K extends keyof FormData>(field: K, value: FormData[K]) {
    const formData = this.state.formData;
    this.setState({
      formData: {
        ...formData,
        [field]: value
      }
    })
  }

  setName = (ev: ChangeEvent<HTMLInputElement>) => this.updateFormData('name', ev.target.value);
  setUrl = (ev: ChangeEvent<HTMLInputElement>) => this.updateFormData('iconUrl', ev.target.value);
  setDescription = (ev: ChangeEvent<HTMLTextAreaElement>) => this.updateFormData('description', ev.target.value);
  setComments = (ev: ChangeEvent<HTMLTextAreaElement>) => this.updateFormData('comments', ev.target.value);
  setTileColor = (ev: ChangeEvent<HTMLInputElement>) => this.updateFormData('tileColor', ev.target.value);
  setLastUpdate = (ev: ChangeEvent<HTMLInputElement>) => this.updateFormData('lastUpdate', ev.target.value);
  setTenantId = (ev: React.MouseEvent<HTMLLIElement> | KeyboardEvent, tenantId: number | string | undefined | TenantDTO) => {this.updateFormData('tenantId', tenantId as number | undefined);}
  setDeleted = (ev: ChangeEvent<HTMLInputElement>) => this.updateFormData('deleted', ev.target.checked);


  render() {
    return (
      <React.Fragment>
        <ProgressBar
          currentStep={this.state.progress.currentStep}
          totalSteps={this.state.progress.totalSteps}
        />
        <BreadcrumbControls
          items={[
            { label: 'Providers', path: '/provider' },
            { label: `${!isEditPage(Number(this.state.providerId)) ? 'adding' : 'editing'} provider ${this.state.formData.name}` },
          ]}
          status={this.state.pageStatus}
        />
        <div className="flex-row">
          <div className="column">
            <div className="card">
              <TextControl
                label="Name"
                type="text"
                name="name"
                id="name"
                value={this.state.formData.name}
                onChange={this.setName}
                disabled={!loadComplete(this.state.progress)}
                br={true}
                required={true}
                onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
                error={this.state.formErrors.name}
              />
              <div className="flex-row fill">
                <div className="column">
                  <TextControl
                    label="File Name"
                    type="text"
                    name="iconUrl"
                    id="iconUrl"
                    value={this.state.formData.iconUrl}
                    onChange={this.setUrl}
                    disabled={!loadComplete(this.state.progress)}
                    br={true}
                    onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
                  />
                </div>
                <div className="column flex-v-center">
                  <img
                    className="thumbnail padded"
                    src={process.env.REACT_APP_INVENTORY_PATH + this.state.formData.iconUrl}
                    style={{
                      "backgroundColor": this.state.formData.tileColor
                    }}
                    alt="avatar"
                  />
                </div>
              </div>
              <TextControl
                label="Tile Color"
                type="text"
                name="tileColor"
                id="tileColor"
                value={this.state.formData.tileColor}
                onChange={this.setTileColor}
                disabled={!loadComplete(this.state.progress)}
                br={true}
              />
              <TextAreaControl
                label="Description"
                id="description"
                name="description"
                value={this.state.formData.description}
                onChange={this.setDescription}
                disabled={!loadComplete(this.state.progress)}
                rows={3}
                cols={30}
                onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
              />
              <TextAreaControl
                label="Comments"
                id="comments"
                name="comments"
                value={this.state.formData.comments}
                onChange={this.setComments}
                disabled={!loadComplete(this.state.progress)}
                rows={3}
                cols={30}
                onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
              />
              {isEditPage(Number(this.state.providerId)) &&
                (<TextControl
                  label="Last update"
                  type="date"
                  name="lastUpdate"
                  id="lastUpdate"
                  value={this.state.formData.lastUpdate}
                  onChange={this.setLastUpdate}
                  disabled={true}
                  br={true}
                />)}
              <SelectControl
                idName="tenantId"
                label="Tenant"
                value={(this.state.formData.tenantId !== undefined && this.state.formData.tenantId !== null) ? this.state.formData.tenantId : 0}
                options={[{id: 0, name: 'None'} as TenantDTO, ...(this.state.tenants || [])]}
                disabled={!loadComplete(this.state.progress)}
                onChange={this.setTenantId}
                getValue={(op) => op.id}
                getLabel={(op) => op?.name || 'Unknown tenant'}
                onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
              />
              {isEditPage(Number(this.state.providerId)) ?
                <ToggleControl
                  id="deleted"
                  name="deleted"
                  changeMethod={this.setDeleted}
                  isChecked={this.state.formData.deleted}
                  isDisabled={!loadComplete(this.state.progress)}
                  labelText="Deleted"
                  onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
                />
                : ''
              }
              <div className="form-group">
                <ButtonControl
                  class="primary-button"
                  onClick={(ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                    this.handleSubmit(ev)
                  }}
                  disabled={!loadComplete(this.state.progress)}
                >
                  <RequestStatus status={this.state.formStatus} />
                  <span className="text">Save</span>
                </ButtonControl>
              </div>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default withTransitionEvent(ProviderDetail);

ProviderDetail.contextType = AppContext;